import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  Container,
  Card,
  CardContent,
  Stack,
  Dialog,
  IconButton,
  DialogContent
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShieldIcon from '@mui/icons-material/Shield';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { useLocation } from 'react-router-dom';
import { showMessage } from '../utils';
import { isInApp } from '../utils/environment';

const InviteCode = () => {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const location = useLocation();
  const inviteInfo = location?.state?.inviteInfo || {};
  const inviteCode = inviteInfo?.inviteCode || '';
  const dialogContentRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const longPressTimerRef = useRef(null);
 
  const handleSave = () => {
    setOpenDialog(true);
  };

  const handleLongPressStart = () => {
    setIsSaving(true);
    setSaveProgress(0);
    
    longPressTimerRef.current = setInterval(() => {
      setSaveProgress(prev => {
        const newProgress = prev + (100/3); // 3秒内完成
        if (newProgress >= 100) {
          clearInterval(longPressTimerRef.current);
          saveDialogAsImage();
          return 100;
        }
        return newProgress;
      });
    }, 100); // 每100毫秒更新一次进度
  };
  
  const handleLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearInterval(longPressTimerRef.current);
      if (saveProgress < 100) {
        setIsSaving(false);
        setSaveProgress(0);
      }
    }
  };
  
  const saveDialogAsImage = async () => {
    if (!dialogContentRef.current) return;
    
    try {
      const canvas = await html2canvas(dialogContentRef.current, {
        scale: 2, // 提高图片质量
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      // 转换为图片并下载
      const image = canvas.toDataURL('image/png');
      
      // 移动端保存到相册
      if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        // 创建一个临时链接用于下载
        const link = document.createElement('a');
        link.href = image;
        link.download = `uTaxes-Invite-${inviteCode.replace(/\s/g, '')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // 桌面端下载
        const link = document.createElement('a');
        link.href = image;
        link.download = `uTaxes-Invite-${inviteCode.replace(/\s/g, '')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      setIsSaving(false);
      setShowSavedMessage(true);
      setTimeout(() => {
        setShowSavedMessage(false);
      }, 2000);
    } catch (error) {
      showMessage('Failed to save image', 'error');
      setIsSaving(false);
    }
  };
  
  const handleCopy = () => {
    if (isInApp()) {
      if (window.flutterApp) {
        const copyData = {
          type: 'Copy',
          text: inviteCode
        };
        window.flutterApp.postMessage(JSON.stringify(copyData));
      }
      return;
    }
    navigator?.clipboard?.writeText(inviteCode).then(() => {
      showMessage('Invite code copied to clipboard!', 'success');
    }).catch(err => {
      showMessage('Failed to copy invite code', 'error');
    });
  };
  
  // 组件卸载时清除定时器
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearInterval(longPressTimerRef.current);
      }
    };
  }, []);

  return (
    <Container maxWidth="sm" sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      <Box sx={{ 
        pt: 2, 
        pb: 4, 
        px: 2,
        position: 'relative'
      }}>
        {/* Back Button */}
        {
          !isInApp() && (
            <IconButton 
            sx={{ 
              position: 'absolute', 
              left: 8,
              top: 8,
              padding: '8px'
            }}
            onClick={() => navigate(-1)}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
          </IconButton>
          )
        }
        {/* Header */}
        {
          !isInApp() && (
            <Typography 
            variant="h4" 
            align="center" 
            sx={{ 
              mt: 0.5,
              mb: 3,
              fontSize: 17,
              fontWeight: 600,
              color: '#000',
              letterSpacing: '-0.5px'
            }}
          >
            Invite Code
          </Typography>
          )
        }


        <Typography 
          variant="h2" 
          align="center" 
          sx={{ 
            fontSize: 28,
            fontWeight: 700,
            mb: 2,
            color: '#000',
            letterSpacing: '-0.5px'
          }}
        >
          Earn Money By Refer
        </Typography>

        <Typography 
          variant="body1" 
          align="center" 
          sx={{ 
            mb: 1.5,
            color: 'rgba(0, 0, 0, 0.6)',
            fontSize: 15,
            lineHeight: 1.4
          }}
        >
          Love, Peace of Mind 
        </Typography>

        <Typography 
          variant="body1" 
          align="center" 
          sx={{ 
            mb: 3,
            color: 'rgba(0, 0, 0, 0.6)',
            fontSize: 15,
            lineHeight: 1.4
          }}
        >
          User Name invites you to join
        </Typography>
        
        {/* Buttons */}
        <Stack 
          direction="row" 
          spacing={2} 
          sx={{ mb: 3 }}
        >
          <Button 
            variant="outlined"
            fullWidth
            onClick={handleCopy}
            sx={{
              borderRadius: '8px',
              py: 1.25,
              border: '1px solid rgba(0, 0, 0, 0.12)',
              color: '#000',
              bgcolor: '#fff',
              fontSize: 15,
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(0, 0, 0, 0.12)',
              }
            }}
          >
            Copied
          </Button>
          <Button 
            variant="contained"
            fullWidth
            onClick={handleSave}
            sx={{
              borderRadius: '8px',
              py: 1.25,
              bgcolor: '#007AFF',
              fontSize: 15,
              fontWeight: 500,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#0066CC',
                boxShadow: 'none'
              }
            }}
          >
            Save
          </Button>
        </Stack>

        {/* Invite Code Card */}
        <Card sx={{ 
          mb: 4,
          borderRadius: '8px',
          boxShadow: 'none',
          bgcolor: 'white',
          position: 'relative',
          overflow: 'visible',
          border: '1px solid rgba(0, 0, 0, 0.06)',
        }}>
          {/* 顶部蓝色渐变条 */}
          <Box
            sx={{
              position: 'absolute',
              top: -3,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '90%',
              height: '8px',
              background: 'linear-gradient(90deg, #579AFF 0%, #2979FF 100%)',
              borderRadius: '8px',
              zIndex: 2,
              boxShadow: '0px 2px 8px rgba(0, 122, 255, 0.15)'
            }}
          />
          
          <CardContent sx={{ 
            pt: 4,
            pb: 3,
            px: 2.5,
            position: 'relative'
          }}>
            {/* Invite Code with background */}
            <Box
              sx={{
                position: 'relative',
                mb: 4,
                mx: -1,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  transform: 'translateY(-50%)',
                  height: '120px',
                  background: 'linear-gradient(180deg, rgba(235, 245, 255, 0.95) 0%, rgba(235, 245, 255, 0.8) 100%)',
                  borderRadius: '8px',
                  zIndex: 0
                }
              }}
            >
              <Typography 
                variant="h3" 
                align="center" 
                sx={{ 
                  fontSize: 32,
                  fontWeight: 600,
                  letterSpacing: '2px',
                  color: '#000',
                  py: 3.5,
                  position: 'relative',
                  zIndex: 1
                }}
              >
                {inviteCode}
              </Typography>
            </Box>

            {/* Features List - 调整图标背景和间距 */}
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box 
                  sx={{ 
                    mr: 2.5,
                    borderRadius: '8px',
                    bgcolor: '#F2EEFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 42,
                    height: 42,
                    flexShrink: 0
                  }}
                >
                  <BookmarkIcon sx={{ color: '#7C4DFF', fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography 
                    variant="h6"
                    sx={{
                      fontSize: 17,
                      fontWeight: 600,
                      mb: 0.5,
                      color: '#000',
                      letterSpacing: '-0.3px'
                    }}
                  >
                    Guaranteed Maximum Refund
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{
                      color: '#8E8E93',
                      fontSize: 14,
                      lineHeight: 1.4
                    }}
                  >
                    "Average $1,283 More in Refunds" - Verified 2023 User Data
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box 
                  sx={{ 
                    mr: 2.5,
                    borderRadius: '8px',
                    bgcolor: '#E3F2FD',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 42,
                    height: 42,
                    flexShrink: 0
                  }}
                >
                  <ShieldIcon sx={{ color: '#2196F3', fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography 
                    variant="h6"
                    sx={{
                      fontSize: 17,
                      fontWeight: 600,
                      mb: 0.5,
                      color: '#000',
                      letterSpacing: '-0.3px'
                    }}
                  >
                    5-Star Defense System
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{
                      color: '#8E8E93',
                      fontSize: 14,
                      lineHeight: 1.4
                    }}
                  >
                    Expert Error Detection • CPA Review • Audit Protection
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box 
                  sx={{ 
                    mr: 2.5,
                    borderRadius: '8px',
                    bgcolor: '#FFF3E0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 42,
                    height: 42,
                    flexShrink: 0
                  }}
                >
                  <FileUploadIcon sx={{ color: '#FF9800', fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography 
                    variant="h6"
                    sx={{
                      fontSize: 17,
                      fontWeight: 600,
                      mb: 0.5,
                      color: '#000',
                      letterSpacing: '-0.3px'
                    }}
                  >
                    Effortless Tax Filing
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{
                      color: '#8E8E93',
                      fontSize: 14,
                      lineHeight: 1.4
                    }}
                  >
                    Upload W-2 via Mobile
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Typography 
          variant="body2" 
          align="center" 
          sx={{ 
            color: '#666',
            fontSize: 13
          }}
        >
          Please search "uTaxes" in the app store to download the app.
        </Typography>
      </Box>

      {/* Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: '#fff',
            borderRadius: '8px',
            m: 2,
            mt: '20px',
            height: '90%'
          }
        }}
      >
        <Box sx={{ 
          bgcolor: '#fff', 
          height: '100%',
          pt: 4,
          px: 3,
          position: 'relative'
        }}>
          {/* 长按提示 */}
          <Typography 
            variant="body2" 
            align="center"
            sx={{
              position: 'absolute',
              top: 12,
              left: 0,
              right: 0,
              fontSize: 13,
              color: 'rgba(0, 0, 0, 0.5)',
              mb: 1
            }}
          >
            长按3秒保存到相册
          </Typography>
          
          {/* 保存进度指示器 */}
          {isSaving && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              zIndex: 10
            }}>
              <Box sx={{
                height: '100%',
                width: `${saveProgress}%`,
                bgcolor: '#007AFF',
                borderRadius: '2px',
                transition: 'width 0.1s linear'
              }} />
            </Box>
          )}
          
          {/* 保存成功提示 */}
          {showSavedMessage && (
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              color: '#fff',
              py: 1.5,
              px: 3,
              borderRadius: 2,
              zIndex: 20
            }}>
              <Typography variant="body1">已保存到相册</Typography>
            </Box>
          )}
          
          {/* 可保存的内容区域 */}
          <Box 
            ref={dialogContentRef}
            sx={{ 
              pt: 2,
              pb: 4
            }}
            onTouchStart={handleLongPressStart}
            onTouchEnd={handleLongPressEnd}
            onTouchCancel={handleLongPressEnd}
            onMouseDown={handleLongPressStart}
            onMouseUp={handleLongPressEnd}
            onMouseLeave={handleLongPressEnd}
          >
            <Box 
              component="img"
              src={require("../assets/images/none-text-logo.png")} 
              sx={{
                width: 60,
                height: 60,
                display: 'block',
                mx: 'auto',
                mb: 2.5
              }}
            />
            <Typography 
              variant="h5" 
              align="center"
              sx={{
                fontSize: 28,
                fontWeight: 600,
                mb: 1.5,
                color: '#000',
                letterSpacing: '-0.5px'
              }}
            >
              uTaxes
            </Typography>
            <Typography 
              variant="body1" 
              align="center"
              sx={{
                fontSize: 17,
                color: 'rgba(0, 0, 0, 0.6)',
                mb: 1,
                letterSpacing: '-0.3px'
              }}
            >
              Love, Peace of Mind 
            </Typography>
            <Typography 
              variant="body1" 
              align="center"
              sx={{
                fontSize: 17,
                color: 'rgba(0, 0, 0, 0.6)',
                mb: 4,
                letterSpacing: '-0.3px'
              }}
            >
              User Name invites you to join
            </Typography>

            {/* Invite Code Card in Dialog */}
            <Card sx={{ 
              borderRadius: '8px',
              boxShadow: 'none',
              bgcolor: '#fff',
              position: 'relative',
              overflow: 'visible',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}>
              {/* 顶部蓝色渐变条 */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -3,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '90%',
                  height: '8px',
                  background: 'linear-gradient(90deg, #579AFF 0%, #2979FF 100%)',
                  borderRadius: '8px',
                  zIndex: 2,
                  boxShadow: '0px 2px 8px rgba(0, 122, 255, 0.15)'
                }}
              />
              
              <CardContent sx={{ 
                pt: 4,
                pb: 3,
                px: 2.5
              }}>
                {/* Invite Code with background */}
                <Box
                  sx={{
                    position: 'relative',
                    mb: 4,
                    mx: -1,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: 0,
                      right: 0,
                      transform: 'translateY(-50%)',
                      height: '120px',
                      background: 'linear-gradient(180deg, rgba(235, 245, 255, 0.95) 0%, rgba(235, 245, 255, 0.8) 100%)',
                      borderRadius: '8px',
                      zIndex: 0
                    }
                  }}
                >
                  <Typography 
                    variant="h3" 
                    align="center" 
                    sx={{ 
                      fontSize: 32,
                      fontWeight: 600,
                      letterSpacing: '2px',
                      color: '#000',
                      py: 3.5,
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    {inviteCode}
                  </Typography>
                </Box>

                {/* Features List */}
                <Stack spacing={3}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box 
                      sx={{ 
                        mr: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: '#E8E3FF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 44,
                        height: 44,
                        flexShrink: 0
                      }}
                    >
                      <BookmarkIcon sx={{ color: '#7C4DFF', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography 
                        variant="h6"
                        sx={{
                          fontSize: 17,
                          fontWeight: 600,
                          mb: 0.5,
                          color: '#000',
                          letterSpacing: '-0.4px'
                        }}
                      >
                        Guaranteed Maximum Refund
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{
                          color: 'rgba(0, 0, 0, 0.6)',
                          fontSize: 15,
                          lineHeight: 1.4,
                          letterSpacing: '-0.3px'
                        }}
                      >
                        "Average $1,283 More in Refunds" - Verified 2023 User Data
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box 
                      sx={{ 
                        mr: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: '#E3F2FD',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 44,
                        height: 44,
                        flexShrink: 0
                      }}
                    >
                      <ShieldIcon sx={{ color: '#2196F3', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography 
                        variant="h6"
                        sx={{
                          fontSize: 17,
                          fontWeight: 600,
                          mb: 0.5,
                          color: '#000',
                          letterSpacing: '-0.4px'
                        }}
                      >
                        5-Star Defense System
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{
                          color: 'rgba(0, 0, 0, 0.6)',
                          fontSize: 15,
                          lineHeight: 1.4,
                          letterSpacing: '-0.3px'
                        }}
                      >
                        Expert Error Detection • CPA Review • Audit Protection
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box 
                      sx={{ 
                        mr: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: '#FFF3E0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 44,
                        height: 44,
                        flexShrink: 0
                      }}
                    >
                      <FileUploadIcon sx={{ color: '#FF9800', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography 
                        variant="h6"
                        sx={{
                          fontSize: 17,
                          fontWeight: 600,
                          mb: 0.5,
                          color: '#000',
                          letterSpacing: '-0.4px'
                        }}
                      >
                        Effortless Tax Filing
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{
                          color: 'rgba(0, 0, 0, 0.6)',
                          fontSize: 15,
                          lineHeight: 1.4,
                          letterSpacing: '-0.3px'
                        }}
                      >
                        Upload W-2 via Mobile
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Dialog>
    </Container>
  );
};

export default InviteCode;
